// Google Analytics utility functions for tracking user interactions
// Tracking ID: G-H36NGCPT6X

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}

// Track page views
export const trackPageView = (page_title: string, page_location: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('config', 'G-H36NGCPT6X', {
      page_title,
      page_location,
      send_page_view: true,
    });
  }
};

// Track section navigation
export const trackSectionView = (section_name: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'section_view', {
      event_category: 'navigation',
      event_label: section_name,
      value: 1,
    });
  }
};

// Track gallery interactions
export const trackGalleryInteraction = (action: string, image_title: string, category: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'gallery_interaction', {
      event_category: 'gallery',
      event_label: `${action}: ${image_title}`,
      custom_parameters: {
        action,
        image_title,
        image_category: category,
      },
    });
  }
};

// Track video engagement
export const trackVideoEngagement = (action: string, video_title: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'video_engagement', {
      event_category: 'video',
      event_label: `${action}: ${video_title}`,
      custom_parameters: {
        action,
        video_title,
      },
    });
  }
};

// Track timeline interactions
export const trackTimelineEvent = (year: string, event_title: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'timeline_interaction', {
      event_category: 'timeline',
      event_label: `${year}: ${event_title}`,
      custom_parameters: {
        timeline_year: year,
        timeline_event: event_title,
      },
    });
  }
};

// Track quote interactions
export const trackQuoteInteraction = (author: string, category: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'quote_interaction', {
      event_category: 'quotes',
      event_label: `${author} - ${category}`,
      custom_parameters: {
        quote_author: author,
        quote_category: category,
      },
    });
  }
};

// Track button clicks
export const trackButtonClick = (button_name: string, section: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'button_click', {
      event_category: 'engagement',
      event_label: `${button_name} in ${section}`,
      custom_parameters: {
        button_name,
        section,
      },
    });
  }
};

// Track scroll depth
export const trackScrollDepth = (depth_percentage: number) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'scroll_depth', {
      event_category: 'engagement',
      event_label: `${depth_percentage}% scroll`,
      value: depth_percentage,
    });
  }
};

// Track awards section engagement
export const trackAwardInteraction = (award_title: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'award_interaction', {
      event_category: 'awards',
      event_label: award_title,
      custom_parameters: {
        award_title,
      },
    });
  }
};

// Track biography section reading
export const trackBiographyEngagement = (section: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'biography_engagement', {
      event_category: 'biography',
      event_label: section,
      custom_parameters: {
        biography_section: section,
      },
    });
  }
};

// Track search-related events
export const trackSearch = (search_term: string, results_count: number) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'search', {
      search_term,
      event_category: 'site_search',
      custom_parameters: {
        search_term,
        results_count,
      },
    });
  }
};

// Track external link clicks
export const trackExternalLink = (url: string, link_text: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'click', {
      event_category: 'outbound_link',
      event_label: url,
      custom_parameters: {
        link_url: url,
        link_text,
      },
    });
  }
};

// Track user engagement time
export const trackEngagementTime = (time_seconds: number, section: string) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'user_engagement', {
      engagement_time_msec: time_seconds * 1000,
      event_category: 'engagement',
      event_label: `${section} - ${time_seconds}s`,
      custom_parameters: {
        section,
        engagement_seconds: time_seconds,
      },
    });
  }
};

// Track mobile vs desktop usage
export const trackDeviceType = () => {
  if (typeof window !== 'undefined' && window.gtag) {
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    window.gtag('event', 'device_type', {
      event_category: 'user_info',
      event_label: isMobile ? 'mobile' : 'desktop',
      custom_parameters: {
        device_type: isMobile ? 'mobile' : 'desktop',
        user_agent: navigator.userAgent,
      },
    });
  }
};

// Track site performance
export const trackPerformance = (metric_name: string, value: number) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'timing_complete', {
      name: metric_name,
      value: Math.round(value),
      event_category: 'performance',
    });
  }
};